import React, { useEffect, useState, useRef } from 'react';
import { Button, Modal, Form } from 'react-bootstrap';
import { AxiosResponse } from 'axios';
import { useNavigate } from 'react-router-dom';
import CollapseHeader from '../../../core/common/collapse-header';
import apiDefinitions from '../../../core/api/apiDefinitions';
import { checkPermission } from '../../../core/permissons/CheckPermissons';
import { showError } from '../../../core/alert/sweetAlertConfig';
import { all_routes } from '../../router/all_routes';
import ViewJobCardModule from './Job_Card/ViewJobCardModule';

/* ===================== Unified Types ===================== */
// Raw DTO from API (uses single user IDs)
interface ApiJobCardDTO {
  id: number;
  maintenanceTaskId: number;
  maintenanceDescription: string;
  locationId: number | null;
  locationName: string | null;
  buildingId: number | null;
  buildingName: string | null;
  floorId: number | null;
  floorName: string | null;
  roomId: number | null;
  roomName: string | null;
  parentJobCardId?: number | null;
  responsibleUserId: number | null;
  accountableUserId: number | null;
  scheduledDateTime: string | null;
  scheduledEndDateTime: string | null;
  status: string;
  comment: string;
}

// Enriched DTO expected by ViewJobCardModule (uses arrays of UserDTO)
interface UserDTO {
  id: number;
  empNumber: string;
  name: string;
}

interface EnrichedJobCardDTO {
  id: number;
  maintenanceTaskId: number | null;
  maintenanceDescription: string | null;
  locationName?: string | null;
  buildingName?: string | null;
  floorName?: string | null;
  roomName?: string | null;
  parentJobCardId: number | null;
  responsiblePersons: UserDTO[];
  accountablePersons: UserDTO[];
  scheduledDateTime: string | null;
  scheduledEndDateTime: string | null;
  status: string;
  comment: string | null;
}

type StatusKey = 'ALL' | 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'NOT_COMPLETED';
type RoleGroup = 'SERVICE_MANAGER' | 'HR_MANAGER' | 'RESPONSIBLE' | 'ACCOUNTABLE' | '';

const STATUS_LABEL: Record<StatusKey, string> = {
  ALL: 'All Jobs',
  PENDING: 'Pending',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  NOT_COMPLETED: 'Not Completed',
};

const statusBadgeClasses = (status: string) => {
  const s = (status || '').toUpperCase();
  if (s === 'PENDING') return 'bg-warning-transparent text-warning border';
  if (s === 'IN_PROGRESS') return 'bg-info-transparent text-info border';
  if (s === 'COMPLETED') return 'bg-success-transparent text-success border';
  if (s === 'CANCELLED') return 'bg-dark-transparent text-dark border';
  if (s === 'NOT_COMPLETED') return 'bg-danger-transparent text-danger border';
  return 'bg-light text-dark border';
};

const formatDate = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' });
};

/* ===================== Donut Chart ===================== */
type Slice = { label: 'Pending' | 'Ongoing' | 'Completed'; value: number; color: string };

const DonutChart: React.FC<{
  data: Slice[];
  centerTitle?: string;
  centerSub?: string;
}> = ({ data, centerTitle, centerSub }) => {
  const total = data.reduce((a, b) => a + b.value, 0);
  const r = 60;
  const cx = 70;
  const cy = 70;
  const stroke = 18;
  const C = 2 * Math.PI * r;

  let acc = 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
      <svg width="160" height="160" viewBox="0 0 140 140">
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#EFF4FB" strokeWidth={stroke} />
        {total === 0 ? (
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="#CBD5E1" strokeWidth={stroke} />
        ) : (
          data.map((s, i) => {
            const frac = s.value / total;
            const dash = frac * C;
            const gap = C - dash;
            const circle = (
              <circle
                key={i}
                cx={cx}
                cy={cy}
                r={r}
                fill="none"
                stroke={s.color}
                strokeWidth={stroke}
                strokeDasharray={`${dash} ${gap}`}
                strokeDashoffset={-(acc * C)}
                style={{ transition: 'stroke-dashoffset .4s ease' }}
              />
            );
            acc += frac;
            return circle;
          })
        )}
        <g textAnchor="middle" dominantBaseline="central">
          <text x={cx} y={cy - 4} style={{ fontSize: 16, fontWeight: 800, fill: '#0F172A' }}>
            {centerTitle ?? (total || 0)}
          </text>
          <text x={cx} y={cy + 14} style={{ fontSize: 11, fill: '#64748B' }}>
            {centerSub ?? 'Total'}
          </text>
        </g>
      </svg>

      <div style={{ display: 'grid', gap: 8, minWidth: 180 }}>
        {data.map((s) => {
          const pct = total ? Math.round((s.value / total) * 100) : 0;
          return (
            <div key={s.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, background: s.color, display: 'inline-block' }} />
                <span style={{ color: '#334155', fontWeight: 700 }}>{s.label}</span>
              </div>
              <div style={{ color: '#1F2937', fontWeight: 800 }}>{pct}%</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ===================== Component ===================== */
const Dashboard: React.FC = () => {
  const [jobCards, setJobCards] = useState<ApiJobCardDTO[]>([]);
  const [filterValue, setFilterValue] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusKey>('ALL');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userLabelCache, setUserLabelCache] = useState<Map<number, string>>(new Map());

  const [dailyDate, setDailyDate] = useState<string | null>(null);
  const [dailyPending, setDailyPending] = useState(0);
  const [dailyOngoing, setDailyOngoing] = useState(0);
  const [dailyCompleted, setDailyCompleted] = useState(0);

  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedJobCard, setSelectedJobCard] = useState<EnrichedJobCardDTO | null>(null);

  const navigate = useNavigate();
  const pageTopRef = useRef<HTMLDivElement>(null);

  const [userGroup, setUserGroup] = useState<RoleGroup>('');
  const [viewTab, setViewTab] = useState<'MY' | 'TODAY'>('MY');

  /* ======================= Styles ======================= */
  const S = {
    filterBar: {
      background: '#fff', border: '1px solid #EEF2F7', borderRadius: 12, padding: 14,
      display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', boxShadow: '0 6px 18px rgba(16,24,40,.06)'
    } as React.CSSProperties,
    grid: {
      marginTop: 18,
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
      gap: 16
    } as React.CSSProperties,
    card: {
      background: '#fff', border: '1px solid #EEF2F7', borderRadius: 14, padding: 16,
      boxShadow: '0 8px 18px rgba(16,24,40,.06)', position: 'relative'
    } as React.CSSProperties,
    cardTopRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 } as React.CSSProperties,
    title: { fontSize: 16, fontWeight: 800, color: '#0F172A', margin: 0 } as React.CSSProperties,
    code: { fontSize: 11, color: '#64748B', marginTop: 2 } as React.CSSProperties,
    line: { display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13, color: '#334155', marginTop: 10 } as React.CSSProperties,
    label: { color: '#6B7280' } as React.CSSProperties,
    desc: { marginTop: 12, fontSize: 13, color: '#475569', lineHeight: 1.4 } as React.CSSProperties,
    footer: { marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' } as React.CSSProperties,
    iconBtn: { width: 36, height: 36, borderRadius: 10, border: 'none' } as React.CSSProperties
  };

  /* ======================= Helpers ======================= */
  const checkToken = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      showError('Authentication token not found. Please log in.');
      navigate(all_routes.login);
      return false;
    }
    return true;
  };

  const shortName = (label: string, takeLast: number = 3): string => {
    if (!label || label === '—') return '—';
    const namePart = label.includes(' - ') ? label.split(' - ').pop() || label : label;
    const parts = namePart.trim().split(/\s+/).filter(Boolean);
    if (parts.length <= takeLast) return namePart.trim();
    return parts.slice(-takeLast).join(' ');
  };

  const detectRoleGroup = (): RoleGroup => {
    if (checkPermission('ROLE_SERVICE_MANAGER')) return 'SERVICE_MANAGER';
    if (checkPermission('ROLE_HR_MANAGER') || checkPermission('ROLE_HUMAN_RESOURCES')) return 'HR_MANAGER';
    if (checkPermission('ROLE_JOBCARD_RESPONSIBLE')) return 'RESPONSIBLE';
    if (checkPermission('ROLE_JOBCARD_ACCOUNTABLE')) return 'ACCOUNTABLE';
    return '';
  };

  const fetchUserGroup = async () => {
    if (!checkToken()) return;
    try {
      const role = detectRoleGroup();
      setUserGroup(role);
      if (!(role === 'SERVICE_MANAGER' || role === 'HR_MANAGER') && viewTab === 'TODAY') {
        setViewTab('MY');
      }
    } catch (err) {
      console.error('Error detecting role:', err);
    }
  };

  const ensureUserLabel = async (id: number | null): Promise<string> => {
    if (!id) return '—';
    if (userLabelCache.has(id)) return userLabelCache.get(id)!;

    try {
      let res = await apiDefinitions.getUsers('1', { filterType: 'id', filterValue: String(id), date: '' });
      if (res?.data?.status === 'S1000') {
        const arr: any[] = res?.data?.data?.users ?? [];
        const exact = arr.find((u: any) => Number(u?.id) === id);
        if (exact) {
          const label = exact.empNumber ? `${exact.empNumber} - ${exact.name || 'Unknown'}` : (exact.name || 'Unknown');
          setUserLabelCache(prev => new Map(prev).set(id, label));
          return label;
        }
      }

      let page = 1;
      while (true) {
        res = await apiDefinitions.getUsers(String(page), { filterType: '', filterValue: '', date: '' });
        if (res?.data?.status !== 'S1000') break;
        const arr: any[] = res?.data?.data?.users ?? [];
        if (!arr.length) break;
        const found = arr.find((u: any) => Number(u?.id) === id);
        if (found) {
          const label = found.empNumber ? `${found.empNumber} - ${found.name || 'Unknown'}` : (found.name || 'Unknown');
          setUserLabelCache(prev => new Map(prev).set(id, label));
          return label;
        }
        page += 1;
      }

      const fallback = `User ${id}`;
      setUserLabelCache(prev => new Map(prev).set(id, fallback));
      return fallback;
    } catch {
      const fallback = `User ${id}`;
      setUserLabelCache(prev => new Map(prev).set(id, fallback));
      return fallback;
    }
  };

  const todayInColombo = (): string =>
    new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Colombo' }).format(new Date());

  /* ======================= Enricher ======================= */
  const enrichJobCard = async (card: ApiJobCardDTO): Promise<EnrichedJobCardDTO> => {
    const respLabel = card.responsibleUserId ? await ensureUserLabel(card.responsibleUserId) : null;
    const accLabel = card.accountableUserId ? await ensureUserLabel(card.accountableUserId) : null;

    const parseLabel = (label: string | null): UserDTO | null => {
      if (!label || label === '—') return null;
      const match = label.match(/^(\S*?)\s*-\s*(.+)$/);
      if (match) {
        return { id: card.responsibleUserId || card.accountableUserId || 0, empNumber: match[1].trim(), name: match[2].trim() };
      }
      return { id: card.responsibleUserId || card.accountableUserId || 0, empNumber: '', name: label };
    };

    return {
      id: card.id,
      maintenanceTaskId: card.maintenanceTaskId,
      maintenanceDescription: card.maintenanceDescription,
      locationName: card.locationName,
      buildingName: card.buildingName,
      floorName: card.floorName,
      roomName: card.roomName,
      parentJobCardId: card.parentJobCardId ?? null,
      responsiblePersons: respLabel ? [parseLabel(respLabel)!] : [],
      accountablePersons: accLabel ? [parseLabel(accLabel)!] : [],
      scheduledDateTime: card.scheduledDateTime,
      scheduledEndDateTime: card.scheduledEndDateTime,
      status: card.status,
      comment: card.comment,
    };
  };

  /* ======================= Fetchers ======================= */
  const fetchMyJobCards = async () => {
    if (!checkToken()) return;
    setIsLoading(true);
    setError(null);
    try {
      const payload = { filterType: '', filterValue: filterValue, date: todayInColombo() };
      const accRes = (apiDefinitions as any).getJobCardsByAccountablePerson ? await (apiDefinitions as any).getJobCardsByAccountablePerson(1, payload) : null;
      const respRes = (apiDefinitions as any).getJobCardsByResponsiblePerson ? await (apiDefinitions as any).getJobCardsByResponsiblePerson(1, payload) : null;

      const accList: ApiJobCardDTO[] = accRes?.data?.status === 'S1000' ? accRes.data.data || [] : [];
      const respList: ApiJobCardDTO[] = respRes?.data?.status === 'S1000' ? respRes.data.data || [] : [];

      const map = new Map<number, ApiJobCardDTO>();
      [...accList, ...respList].forEach(j => map.set(j.id, j));
      const merged = Array.from(map.values());

      const ids = new Set<number>();
      merged.forEach(c => {
        if (c.responsibleUserId) ids.add(c.responsibleUserId);
        if (c.accountableUserId) ids.add(c.accountableUserId);
      });
      await Promise.all(Array.from(ids).map(id => ensureUserLabel(id)));

      setJobCards(merged);
      if (!merged.length) setError('No job cards found.');
    } catch (err: any) {
      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        navigate(all_routes.login);
      }
      showError(err.response?.data?.statusDescription || 'Error fetching my job cards');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTodaysJobCards = async () => {
    if (!checkToken()) return;
    setIsLoading(true);
    setError(null);
    try {
      const payload = { filterType: '', filterValue: filterValue, date: todayInColombo() };
      const response: AxiosResponse = await apiDefinitions.getAllJobCards(1, payload);
      if (response?.data?.status === 'S1000') {
        const list: ApiJobCardDTO[] = response.data.data || [];
        const ids = new Set<number>();
        list.forEach(c => {
          if (c.responsibleUserId) ids.add(c.responsibleUserId);
          if (c.accountableUserId) ids.add(c.accountableUserId);
        });
        await Promise.all(Array.from(ids).map(id => ensureUserLabel(id)));
        setJobCards(list);
        if (!list.length) setError('No job cards found for today.');
      } else {
        showError(response?.data?.statusDescription || 'Failed to fetch today’s job cards');
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        navigate(all_routes.login);
      }
      showError(err.response?.data?.statusDescription || 'Error fetching job cards');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDailySummary = async () => {
    try {
      const res = await apiDefinitions.getDailyJobCardSummary();
      if (res?.data?.status === 'S1000') {
        const arr = res?.data?.data?.dailySummaryData || [];
        if (arr.length) {
          const d = arr[0];
          setDailyDate(d?.date || null);
          setDailyPending(Number(d?.pending || 0));
          setDailyOngoing(Number(d?.ongoing || 0));
          setDailyCompleted(Number(d?.completed || 0));
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  /* ======================= Derived ======================= */
  const counts = React.useMemo(() => {
    const c = { total: 0, pending: 0, inProgress: 0, completed: 0, cancelled: 0, notCompleted: 0 };
    c.total = jobCards.length;
    jobCards.forEach(j => {
      const s = j.status.toUpperCase();
      if (s === 'PENDING') c.pending++;
      else if (s === 'IN_PROGRESS') c.inProgress++;
      else if (s === 'COMPLETED') c.completed++;
      else if (s === 'CANCELLED') c.cancelled++;
      else if (s === 'NOT_COMPLETED') c.notCompleted++;
    });
    return c;
  }, [jobCards]);

  const FILTER_META = {
    ALL: { label: 'All Jobs', icon: 'ti-notebook', on: 'bg-primary text-white', off: 'bg-primary-transparent text-primary' },
    PENDING: { label: 'Pending', icon: 'ti-hourglass', on: 'bg-warning text-white', off: 'bg-warning-transparent text-warning' },
    IN_PROGRESS: { label: 'In Progress', icon: 'ti-player-play', on: 'bg-info text-white', off: 'bg-info-transparent text-info' },
    COMPLETED: { label: 'Completed', icon: 'ti-check', on: 'bg-success text-white', off: 'bg-success-transparent text-success' },
    CANCELLED: { label: 'Cancelled', icon: 'ti-square-x', on: 'bg-dark text-white', off: 'bg-dark-transparent text-dark' },
    NOT_COMPLETED: { label: 'Not Completed', icon: 'ti-alert-triangle', on: 'bg-danger text-white', off: 'bg-danger-transparent text-danger' },
  };

  const getFilterCount = (key: StatusKey) => {
    switch (key) {
      case 'ALL': return counts.total;
      case 'PENDING': return counts.pending;
      case 'IN_PROGRESS': return counts.inProgress;
      case 'COMPLETED': return counts.completed;
      case 'CANCELLED': return counts.cancelled;
      case 'NOT_COMPLETED': return counts.notCompleted;
      default: return 0;
    }
  };

  const filteredCards = React.useMemo(() => {
    let list = [...jobCards];
    if (statusFilter !== 'ALL') list = list.filter(j => j.status.toUpperCase() === statusFilter);
    if (filterValue) {
      const q = filterValue.toLowerCase();
      list = list.filter(j => j.maintenanceDescription.toLowerCase().includes(q) || String(j.id).includes(filterValue));
    }
    return list;
  }, [jobCards, statusFilter, filterValue]);

  /* ======================= Effects ======================= */
  useEffect(() => { fetchUserGroup(); }, []);
  useEffect(() => {
    if (!userGroup) return;
    if (viewTab === 'MY') fetchMyJobCards();
    else { fetchTodaysJobCards(); fetchDailySummary(); }
  }, [userGroup, viewTab, filterValue]);

  /* ======================= Card Item ======================= */
  const JobCardItem: React.FC<{ card: ApiJobCardDTO }> = ({ card }) => {
    const [respLabel, setRespLabel] = useState('Loading...');
    const [accLabel, setAccLabel] = useState('Loading...');

    useEffect(() => {
      ensureUserLabel(card.responsibleUserId).then(setRespLabel);
      ensureUserLabel(card.accountableUserId).then(setAccLabel);
    }, [card]);

    return (
      <div style={S.card}>
        <div style={S.cardTopRow}>
          <div>
            <h3 className="text-truncate" style={S.title} title={card.maintenanceDescription}>
              {card.maintenanceDescription}
            </h3>
            <div style={S.code}>JC{String(card.id).padStart(3, '0')}</div>
          </div>
          <div className={`px-2 py-1 rounded-pill fw-bold ${statusBadgeClasses(card.status)}`} style={{ fontSize: 12 }}>
            {card.status.replace('_', ' ')}
          </div>
        </div>

        <div style={S.line} className="mt-2">
          <span style={S.label}>Location</span>
          <span className="text-truncate">
            {[card.locationName, card.buildingName, card.floorName, card.roomName].filter(Boolean).join(' > ') || 'N/A'}
          </span>
        </div>

        <div style={S.line}>
          <span style={S.label}>Assignee</span>
          <span className="badge rounded-pill bg-success-transparent text-success" title={respLabel}>
            <i className="ti ti-user me-1" />
            {shortName(respLabel)}
          </span>
        </div>

        {userGroup !== 'SERVICE_MANAGER' && userGroup !== 'HR_MANAGER' && (
          <div style={S.line}>
            <span style={S.label}>Accountable</span>
            <span className="badge rounded-pill bg-warning-transparent text-warning" title={accLabel}>
              <i className="ti ti-user-check me-1" />
              {shortName(accLabel)}
            </span>
          </div>
        )}

        <div className="border-top pt-2 mt-2" style={S.desc}>
          {(card.comment?.trim()) || card.maintenanceDescription}
        </div>

        <div style={S.footer}>
          <small className="text-muted">ID: {card.id}</small>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="bg-info text-white d-inline-flex align-items-center justify-content-center"
              style={S.iconBtn}
              title="View"
              onClick={async () => {
                const enriched = await enrichJobCard(card);
                setSelectedJobCard(enriched);
                setShowViewModal(true);
              }}
            >
              <i className="ti ti-eye" />
            </button>
            <button
              type="button"
              className="bg-secondary text-white d-inline-flex align-items-center justify-content-center"
              style={S.iconBtn}
              title="Update Status"
              onClick={() => {
                enrichJobCard(card).then(enriched => {
                  setSelectedJobCard(enriched);
                  setShowUpdateModal(true);
                });
              }}
              disabled={!checkPermission('ROLE_MANAGE_JOB_CARD')}
            >
              <i className="ti ti-edit" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  /* ======================= Status Update Modal ======================= */
  const StatusUpdateModal: React.FC = () => {
    const [status, setStatus] = useState(selectedJobCard?.status || 'PENDING');
    const [comment, setComment] = useState('');

    useEffect(() => {
      setStatus(selectedJobCard?.status || 'PENDING');
      setComment('');
    }, [selectedJobCard]);

    const handleStatusUpdate = async () => {
      if (!selectedJobCard) return;
      try {
        const payload = { jobCardId: selectedJobCard.id, status, accountablePersonComment: comment || 'Status updated' };
        const response = await apiDefinitions.changeJobCardStatus(payload);
        if (response.data.status === 'S1000') {
          setShowUpdateModal(false);
          setSelectedJobCard(null);
          if (viewTab === 'MY') fetchMyJobCards();
          else { fetchTodaysJobCards(); fetchDailySummary(); }
        } else {
          showError(response.data.statusDescription || 'Failed to update');
        }
      } catch (err: any) {
        showError(err?.response?.data?.statusDescription || 'Error updating status');
      }
    };

    return (
      <Modal show={showUpdateModal} onHide={() => setShowUpdateModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Update Status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Status</Form.Label>
            <Form.Select value={status} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setStatus(e.target.value)}>
              <option value="PENDING">Pending</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="NOT_COMPLETED">Not Completed</option>
              <option value="COMPLETED">Completed</option>
              <option value="CANCELLED">Cancelled</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Comment</Form.Label>
            <Form.Control as="textarea" rows={3} value={comment} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setComment(e.target.value)} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUpdateModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleStatusUpdate}>Update</Button>
        </Modal.Footer>
      </Modal>
    );
  };

  /* ======================= Render ======================= */
  const chartSlices: Slice[] = [
    { label: 'Pending', value: dailyPending, color: '#F59E0B' },
    { label: 'Ongoing', value: dailyOngoing, color: '#0EA5E9' },
    { label: 'Completed', value: dailyCompleted, color: '#10B981' },
  ];

  const isPrivileged = userGroup === 'SERVICE_MANAGER' || userGroup === 'HR_MANAGER';

  return (
    <div className="page-wrapper bg-light" ref={pageTopRef}>
      <div className="content">
        <div className="row">
          <div className="page-header d-flex align-items-center justify-content-between">
            <h4 className="page-title mb-0">Facility Maintenance / Job Card Dashboard</h4>
            <div className="d-flex gap-2">
              {isPrivileged && (
                <Button variant={viewTab === 'TODAY' ? 'primary' : 'outline-primary'} onClick={() => setViewTab('TODAY')}>
                  Today’s Job Cards
                </Button>
              )}
              <Button variant={viewTab === 'MY' ? 'primary' : 'outline-primary'} onClick={() => setViewTab('MY')}>
                My Job Cards
              </Button>
            </div>
          </div>
        </div>

        {viewTab === 'TODAY' && (
          <div className="card">
            <div className="card-body d-flex flex-wrap align-items-center justify-content-between" style={{ gap: 16 }}>
              <div>
                <h6 className="mb-1" style={{ color: '#0F172A', fontWeight: 800 }}>Today’s Job Card Summary</h6>
                <div className="text-muted" style={{ fontSize: 12 }}>
                  {dailyDate ? `Date: ${formatDate(dailyDate)}` : 'No data for today'}
                </div>
              </div>
              <DonutChart data={chartSlices} centerTitle={String(dailyPending + dailyOngoing + dailyCompleted)} centerSub="Total" />
            </div>
          </div>
        )}

        <div className="card mt-3">
          <div className="card-body" style={S.filterBar}>
            <div className="d-flex flex-wrap gap-2">
              {(Object.keys(STATUS_LABEL) as StatusKey[]).map(key => {
                const meta = FILTER_META[key];
                const active = statusFilter === key;
                return (
                  <button
                    key={key}
                    onClick={() => setStatusFilter(key)}
                    className={`d-inline-flex align-items-center gap-2 border-0 px-3 py-2 rounded-pill fw-bold shadow-sm ${active ? meta.on : meta.off}`}
                    style={{ fontSize: 12 }}
                  >
                    <i className={`ti ${meta.icon}`} />
                    <span>{meta.label}</span>
                    <span className="badge rounded-pill bg-light text-dark">{getFilterCount(key)}</span>
                  </button>
                );
              })}
            </div>
            <div className="ms-auto" style={{ minWidth: 220 }}>
              <input className="form-control" placeholder="Search by description or ID…" value={filterValue} onChange={e => setFilterValue(e.target.value)} />
            </div>
          </div>
        </div>

        <div className="mt-3">
          {isLoading ? (
            <div className="text-center my-4"><div className="spinner-border" role="status" /></div>
          ) : error ? (
            <div className="alert alert-info">{error}</div>
          ) : filteredCards.length === 0 ? (
            <div className="alert alert-info">No job cards found.</div>
          ) : (
            <div style={S.grid}>
              {filteredCards.map(card => <JobCardItem key={card.id} card={card} />)}
            </div>
          )}
        </div>

        {showUpdateModal && <StatusUpdateModal />}

        {showViewModal && selectedJobCard && (
          <ViewJobCardModule
            show={showViewModal}
            onHide={() => { setShowViewModal(false); setSelectedJobCard(null); }}
            jobCard={selectedJobCard}
          />
        )}
      </div>
    </div>
  );
};

export default Dashboard;